/**
 * Projectiles — every basic-attack bullet on screen, and the melee swings.
 *
 * ── Where a drawn bullet comes from ────────────────────────────────────────
 *
 * Snapshots do NOT carry projectiles, and this module is why they do not have
 * to. A bullet's whole flight is a function of the cast: origin, frozen yaw,
 * the roster's speed, range and pellet fan, and the map's walls. All of that
 * is on the client already, so a bullet is REPLAYED here from the
 * `ability_cast` event (remote shooters) or from the predictor's own cast
 * event (the local shooter, the same frame the button went down). The sim's
 * `traceShot` ends the drawn bullet on the wall the real one stops at, and
 * the server's `hit` event ends it on the body the real one hit.
 *
 * The bandwidth this saves is the argument. A snapshot carrying projectiles
 * costs, per bullet, ~9 numbers plus keys — call it 90 bytes packed — and
 * eight players with three pips of up to five pellets is 120 bullets, or
 * ~11 KB per snapshot, ~160 KB/s at 15 Hz on a phone. The cast event those
 * bullets derive from is one message of ~60 bytes per press: at three presses
 * a second per player, under 1.5 KB/s for the whole room. Two orders of
 * magnitude, and the events already exist.
 *
 * What is NOT here: any decision. A bullet drawn here never damages anyone.
 * It is presentation of a server decision (a cast that happened, a hit that
 * happened); when the two disagree by a few centimetres, the server's point
 * wins and the drawn bullet is simply ended there.
 *
 * ── Rendering: a bullet is an OBJECT ───────────────────────────────────────
 *
 * One `InstancedMesh` of a capsule, fixed at MAX_BULLETS instances, stretched
 * along each bullet's velocity (faster rounds are longer tracers). The
 * material is a cel ramp on the view-space normal: a white-hot stripe where
 * the capsule faces the camera, the character's colour on its flanks, and an
 * INK rim at the silhouette — so every round reads as a fat painted tracer in
 * its shooter's colour on the bright deck with no bloom at all. (Pass 1 ran
 * the colour × 3 through an unmapped basic material and every round clipped
 * to plain white.) The tip is hotter than the tail.
 *
 * Every round also owns a continuous tapered RIBBON behind it (`fx.ribbon`,
 * a strip sampled from the round's own path, never a chain of sprites) and
 * casts a BLOB SHADOW on the deck under it each frame — from a top-down
 * camera the shadow is what says the round is in the air.
 *
 * A fixed instance count means the pipeline is compiled once; `count` is the
 * only thing that changes per frame. Node materials only — the renderer is
 * WebGPU.
 */

import * as THREE from 'three/webgpu';
import { Fn, float, vec3, mix, smoothstep, normalView, positionGeometry, instancedBufferAttribute } from 'three/tsl';
import { CHARACTERS, pelletOffsetRad, isBurst } from '@chaos/protocol';
import { traceShot, muzzleClear, PROJECTILE_MUZZLE_HEIGHT, PROJECTILE_SPAWN_OFFSET, PLAYER_HALF_HEIGHT } from '@chaos/sim';
import * as fx from '../vfx/fx.js';
import { palette, identity } from '../vfx/CelLook.js';
import { registerDiagnostics } from '../performance/runtime.js';

/** Enough for eight shooters emptying a burst each with room to spare. */
const MAX_BULLETS = 192;
/** How far a server hit may land from a drawn bullet and still be "that bullet". */
const HIT_MATCH_RADIUS = 3.5;
/** A round is never drawn thinner than this (diameter): a 0.2-radius pellet is a fat 0.5 tracer. */
const MIN_WIDTH = 0.5;
/** Tracer length as a multiple of width, by speed: a slow orb is nearly round, a sniper round is a streak. */
function stretchFor(speed) {
  return Math.min(3.6, Math.max(1.25, speed / 13));
}
/** The ribbon behind a round: as wide as the round, as long as ~85 ms of its flight. */
function ribbonLengthFor(speed) {
  return Math.min(4.2, Math.max(1.4, speed * 0.085));
}

let _scene = null;
let _mesh = null;
let _material = null;
let _tintAttr = null;
/** Live bullets. Plain objects, reused from a free list so a volley never allocates. */
const _live = [];
const _free = [];
/** (id, out) => out | null — the drawn body. See initProjectiles. */
let _resolveCaster = null;
const _body = new THREE.Vector3();
const _muzzle = new THREE.Vector3();
/** Volleys waiting on their wind-up, and burst pellets on their ladder. */
const _pending = [];
const _stats = { spawned: 0, hits: 0, walls: 0, expired: 0 };

const _tmpPos = new THREE.Vector3();
const _tmpQuat = new THREE.Quaternion();
const _tmpScale = new THREE.Vector3();
const _tmpMatrix = new THREE.Matrix4();
const _tmpDir = new THREE.Vector3();
const _tmpColor = new THREE.Color();
const _forward = new THREE.Vector3(0, 0, 1);

/**
 * The tracer material: a cel ramp on how much of the capsule faces the camera
 * (`normalView.z`), plus a hotter nose. The tint is a per-instance attribute
 * of the geometry rather than `instanceColor`, because the ramp needs the raw
 * colour to build white and ink from, not a multiplier on the result.
 */
function buildTracerMaterial(tintAttr) {
  const mat = new THREE.MeshBasicNodeMaterial();
  const tint = instancedBufferAttribute(tintAttr);
  // The core is NOT pushed into the bloom: a round is a painted object, and a
  // blooming tracer is the white blob of pass 1 all over again.
  const c = palette(tint, float(1.15));
  // How much of the tube faces the camera: 1 on the centre line, 0 at the
  // silhouette. Seen from overhead a capsule's whole top half is "facing", so
  // the bands are cut tight: a thin white stripe, a hot band, the body, ink.
  const facing = normalView.z.clamp(0, 1);
  // The capsule is authored along +Z (−1 tail … +1 nose). The nose is hotter.
  const nose = smoothstep(float(0.3), float(0.95), positionGeometry.z);
  mat.colorNode = Fn(() => {
    let col = mix(c.deep, c.body, smoothstep(float(0.5), float(0.62), facing));
    col = mix(col, c.hot, smoothstep(float(0.86), float(0.92), facing).max(nose.mul(smoothstep(float(0.7), float(0.8), facing))));
    col = mix(col, c.core, smoothstep(float(0.965), float(0.985), facing).max(nose.mul(smoothstep(float(0.9), float(0.95), facing))));
    col = mix(col, c.ink, smoothstep(float(0.42), float(0.3), facing));
    return col;
  })();
  mat.fog = false;
  return mat;
}

/**
 * `resolveCaster(id, out)` writes where that player's body is DRAWN right now
 * into `out` and returns it, or returns null. Rounds and muzzle flashes read it
 * at the moment they fire: the sim fires every round from the live body after
 * the wind-up (and down a burst's ladder), so a round drawn from the spot the
 * button was pressed comes out behind a moving shooter.
 */
export function initProjectiles(scene, resolveCaster = null) {
  _scene = scene;
  _resolveCaster = resolveCaster;
  // Authored along +Z so `setFromUnitVectors(+Z, dir)` orients it. A capsule
  // reads as a tracer at every angle; a sphere reads as a ball.
  const geometry = new THREE.CapsuleGeometry(0.5, 1, 4, 12);
  geometry.rotateX(Math.PI / 2);
  _tintAttr = new THREE.InstancedBufferAttribute(new Float32Array(MAX_BULLETS * 3), 3);
  _tintAttr.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute('aTint', _tintAttr);
  _material = buildTracerMaterial(_tintAttr);
  _mesh = new THREE.InstancedMesh(geometry, _material, MAX_BULLETS);
  _mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  _mesh.count = 0;
  _mesh.frustumCulled = false;
  _mesh.renderOrder = 8;
  _mesh.userData.noOutline = true;
  _mesh.name = 'bullets';
  _scene.add(_mesh);
  registerDiagnostics('projectiles', () => ({ live: _live.length, pending: _pending.length, ..._stats }));
}

/**
 * A basic attack was cast. Schedules what it draws.
 *
 * @param {object} args
 * @param {string} args.ownerId
 * @param {string} args.characterId
 * @param {{x:number,y:number,z:number}} args.origin the body centre at the press — used only if
 *   the shooter's drawn body cannot be resolved when a round fires
 * @param {number} args.yaw frozen aim from the event
 * @param {number} args.atMs when the press happened, on performance.now()
 * @param {object|null} args.world a SimWorld to trace walls against (the predictor's)
 */
export function fireBasic({ ownerId, characterId, origin: at, yaw, atMs, world }) {
  const def = CHARACTERS[characterId];
  if (!def) return;
  // Only a fallback for a shooter whose body cannot be found when a round
  // fires (gone, or a page without a resolver). Copied, because the caller's
  // vector is shared scratch that the next cast or hit rewrites.
  const fallback = { x: at.x, y: at.y, z: at.z };
  const ability = def.abilities.basic;
  const color = def.colorHex;
  const dirX = -Math.sin(yaw);
  const dirZ = -Math.cos(yaw);
  const strikeAt = atMs + (ability.windupMs ?? 0);

  // A melee basic (the tank's cone, the assassin's lunge) draws nothing here:
  // AbilityVFX's melee_cone strike is the swing's one caller of fx.slash, at
  // resolve, from the body anchor's live position — which is where a lunge
  // has carried the caster by then.

  if (ability.hit.kind !== 'shot') return;
  const shape = ability.hit;
  const burst = isBurst(shape);
  // A burst flashes per round; a single or a spread flashes once, bigger for a spread.
  const muzzleScale = burst ? 0.7 : shape.pellets > 1 ? 1.4 : 1;
  const aim = { x: dirX, z: dirZ };
  for (let i = 0; i < shape.pellets; i++) {
    const at = burst ? strikeAt + i * shape.burstIntervalMs : strikeAt;
    _pending.push({ at, fire: () => spawnBullet(ownerId, ability, bodyNow(ownerId, fallback), yaw, i, world, color) });
    if (burst || i === 0) {
      _pending.push({
        at,
        fire: () => {
          const b = bodyNow(ownerId, fallback);
          fx.muzzle(_muzzle.set(b.x + dirX * 0.5, b.y + PROJECTILE_MUZZLE_HEIGHT, b.z + dirZ * 0.5), aim, color, muzzleScale);
        },
      });
    }
  }
}

/** Where `ownerId`'s body is drawn now, or `fallback` if it cannot be found. */
function bodyNow(ownerId, fallback) {
  return (_resolveCaster && _resolveCaster(ownerId, _body)) ? _body : fallback;
}

function spawnBullet(ownerId, ability, origin, yaw, pellet, world, color) {
  const shape = ability.hit;
  if (_live.length >= MAX_BULLETS) return;
  const a = yaw + pelletOffsetRad(shape, pellet);
  const dirX = -Math.sin(a);
  const dirZ = -Math.cos(a);
  // The sim drops a round whose barrel is inside a wall; so does the drawing.
  if (world && !muzzleClear(world, origin.x, origin.y + PROJECTILE_MUZZLE_HEIGHT, origin.z, dirX, dirZ)) return;
  const x = origin.x + dirX * PROJECTILE_SPAWN_OFFSET;
  const y = origin.y + PROJECTILE_MUZZLE_HEIGHT;
  const z = origin.z + dirZ * PROJECTILE_SPAWN_OFFSET;
  // The sim's own wall trace, so the drawn bullet dies on the wall the real
  // one dies on. No world (a mockup page) means the full range.
  const maxDist = world ? traceShot(world, x, y, z, dirX, dirZ, shape.maxRange) : shape.maxRange;
  const b = _free.pop() ?? {};
  b.ownerId = ownerId;
  b.x = x;
  b.y = y;
  b.z = z;
  b.floorY = origin.y - PLAYER_HALF_HEIGHT;
  b.dirX = dirX;
  b.dirZ = dirZ;
  b.speed = shape.speed;
  b.radius = shape.radius;
  b.width = Math.max(shape.radius * 2, MIN_WIDTH);
  b.pierce = shape.pierce;
  b.travelled = 0;
  b.maxDist = maxDist;
  b.hitWall = maxDist < shape.maxRange - 1e-6;
  b.color = color;
  // The wake is a little wider than the round; a two-unit orb keeps a sane wake.
  b.ribbon = fx.ribbon(color, Math.min(b.width * 1.15, 1.3), ribbonLengthFor(shape.speed));
  fx.ribbonPush(b.ribbon, b);
  _live.push(b);
  _stats.spawned++;
}

/**
 * The server said `attackerId`'s basic hit something at `point`. End the
 * drawn bullet nearest that point, if one is close enough to be it.
 */
export function endBulletNear(attackerId, point) {
  let best = -1;
  let bestD2 = HIT_MATCH_RADIUS * HIT_MATCH_RADIUS;
  for (let i = 0; i < _live.length; i++) {
    const b = _live[i];
    if (b.ownerId !== attackerId) continue;
    const dx = b.x - point.x;
    const dz = b.z - point.z;
    const d2 = dx * dx + dz * dz;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = i;
    }
  }
  if (best < 0) return false;
  const b = _live[best];
  // A piercing round keeps flying on the server after a body, so the drawn one
  // does too; it still ends on its wall or at its range.
  if (b.pierce) return true;
  // The impact itself is drawn by playHitVFX for every hit, bullet or not;
  // here the drawn bullet simply stops existing.
  _stats.hits++;
  release(best);
  return true;
}

/** The direction of `attackerId`'s drawn bullet nearest `point`, for a directional impact. */
export function bulletDirNear(attackerId, point, out) {
  let best = null;
  let bestD2 = HIT_MATCH_RADIUS * HIT_MATCH_RADIUS;
  for (let i = 0; i < _live.length; i++) {
    const b = _live[i];
    if (b.ownerId !== attackerId) continue;
    const dx = b.x - point.x;
    const dz = b.z - point.z;
    const d2 = dx * dx + dz * dz;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = b;
    }
  }
  if (!best) return null;
  out.x = best.dirX;
  out.z = best.dirZ;
  return out;
}

function release(index) {
  const b = _live[index];
  fx.ribbonRelease(b.ribbon);
  b.ribbon = -1;
  const last = _live.length - 1;
  _live[index] = _live[last];
  _live.pop();
  _free.push(b);
}

const _dir = { x: 0, z: 0 };

/**
 * Advances every drawn bullet by `deltaS` and rewrites the instance buffer.
 * `nowMs` drives the wind-up ladder; `deltaS` is the presentation delta (zero
 * during hitstop, like every other effect).
 */
export function updateProjectiles(nowMs, deltaS) {
  // Fire whatever has come due, in order.
  if (_pending.length > 0) {
    let write = 0;
    for (let i = 0; i < _pending.length; i++) {
      const p = _pending[i];
      if (p.at <= nowMs) p.fire();
      else _pending[write++] = p;
    }
    _pending.length = write;
  }

  if (!_mesh) return;
  for (let i = _live.length - 1; i >= 0; i--) {
    const b = _live[i];
    const step = b.speed * deltaS;
    b.travelled += step;
    if (b.travelled >= b.maxDist) {
      const over = b.travelled - b.maxDist;
      b.x += b.dirX * (step - over);
      b.z += b.dirZ * (step - over);
      fx.ribbonPush(b.ribbon, b);
      // The bullet carries x/y/z, and the kit reads a position without keeping it.
      _dir.x = b.dirX;
      _dir.z = b.dirZ;
      if (b.hitWall) {
        _stats.walls++;
        fx.impact(b, b.color, { onBody: false, dir: _dir, floorY: b.floorY });
      } else {
        _stats.expired++;
        fx.expire(b, b.color);
      }
      release(i);
      continue;
    }
    b.x += b.dirX * step;
    b.z += b.dirZ * step;
    fx.ribbonPush(b.ribbon, b);
    fx.shadow(b, b.floorY, b.width * 1.2, 0.65);
    fx.requestLight(b, b.color, 1.9, 4.2);
  }

  const n = _live.length; // spawnBullet never admits past MAX_BULLETS
  const tint = _tintAttr.array;
  for (let i = 0; i < n; i++) {
    const b = _live[i];
    _tmpPos.set(b.x, b.y, b.z);
    _tmpDir.set(b.dirX, 0, b.dirZ);
    _tmpQuat.setFromUnitVectors(_forward, _tmpDir);
    // The capsule geometry is 1 wide (radius 0.5) and 2 long: scale so the
    // width is the drawn diameter and the length is the tracer stretch.
    const w = b.width;
    _tmpScale.set(w, w, w * stretchFor(b.speed) * 0.5);
    _tmpMatrix.compose(_tmpPos, _tmpQuat, _tmpScale);
    _mesh.setMatrixAt(i, _tmpMatrix);
    // The vivid identity colour (see CelLook.identity).
    identity(b.color, _tmpColor);
    tint[i * 3] = _tmpColor.r;
    tint[i * 3 + 1] = _tmpColor.g;
    tint[i * 3 + 2] = _tmpColor.b;
  }
  _mesh.count = n;
  _mesh.instanceMatrix.needsUpdate = true;
  _tintAttr.needsUpdate = true;
}

/** Drops every drawn bullet and pending volley. A new world must start empty. */
export function clearProjectiles() {
  while (_live.length > 0) release(_live.length - 1);
  _pending.length = 0;
  if (_mesh) {
    _mesh.count = 0;
    _mesh.instanceMatrix.needsUpdate = true;
  }
}

export function disposeProjectiles() {
  clearProjectiles();
  if (_mesh) {
    _mesh.removeFromParent();
    _mesh.geometry.dispose();
    _material?.dispose();
  }
  _mesh = null;
  _material = null;
  _tintAttr = null;
  _scene = null;
}

/** The bullet mesh, for the shader prewarm. */
export function projectileMesh() {
  return _mesh;
}
